/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useState } from 'react';

// Mantine
import {
	ActionIcon,
	Alert,
	Avatar,
	Box,
	Group,
	Image,
	Paper,
	Skeleton,
	Stack,
	Text,
} from '@mantine/core';
import {
	useElementSize,
	useMediaQuery,
} from '@mantine/hooks';

// Styles
import classes from './measurability-project-details.module.css';

// Router
import { useParams } from 'react-router';

// I18n
import { useTranslation } from 'react-i18next';

// Api
import { useMeasurabilityProject } from '@features/catalogs/meausrability-projects/details/api/get-measurability-project-by-id';

// Components
import { Button } from '@components/atoms';

// Icons
import LinkedinLogo from '@assets/icons/linkedin-button.svg';
import XLogo from '@assets/icons/x-button.svg';
import {
	IconCirclePlus,
	IconDownload,
	IconEdit,
	IconEye,
	IconInfoCircle,
	IconQuestionMark,
} from '@tabler/icons-react';

// local components
import { Sites } from '@features/catalogs/meausrability-projects/details/components/Sites';

// constants
import { DescriptionRenderer } from '@components/atoms/DescriptionRenderer';
import { OrganizationProject } from '@components/organisms/OrganizationProject';
import { ACLS } from '@config/constants/acls';
import {
	LINKEDIN_URL,
	NOT_APPLICABLE,
	X_URL,
} from '@config/constants/common';
import { ROLES } from '@config/constants/roles';
import { TSiteCustom } from '@features/catalogs/meausrability-projects/details/components/Sites/Sites';
import { UpdateMeasurabilityProject } from '@features/catalogs/meausrability-projects/list/components/UpdateMeasurabilityProject/UpdateMeasurabilityProject';
import {
	TFileMetadata,
	TSite,
} from '@global-types/index';
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';
import { queryClient } from '@lib/react-query';
import useStore from '@store/index';
import { getCountryName } from '@utils/countries';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import { useSites } from 'shared/api/get-sites';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const MeasurabilityProjectDetails = () => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();

	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const { ref } = useElementSize();

	const { projectId } = useParams();

	const { checkAccess, checkAcl, checkRole } =
		useAuthorization();

	const [siteQuery, setSiteQuery] = useState('');

	const icon = <IconInfoCircle />;

	const {
		setUpdateMeasurabilityProjectFormOpened,
		updateMeasurabilityProjectFormOpened,
		getUser,
	} = useStore();

	const { data: allSites } = useSites({}, siteQuery);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const userData: any = getUser();
	const {
		data,
		user,
		isFetching,
		isError,
		contactProducer,
		sites,
	} = useMeasurabilityProject({
		projectId: projectId ? projectId : '',
	});

	const handleOpenLink = (
		url: string,
		socialMedia: string
	) => {
		switch (socialMedia) {
			case 'linkedin':
				window.open(`${LINKEDIN_URL}/${url}`, '_blank');
				break;
			case 'x':
				window.open(`${X_URL}/${url}`, '_blank');
				break;

			default:
				break;
		}
	};

	if (isError) {
		return (
			<Box className={classes.container} ref={ref}>
				<Paper className={classes.paperError}>
					<Alert
						variant="light"
						color="error"
						title={t('errors.invalidProjectIdTitle')}
						icon={icon}
					>
						{t('errors.invalidProjectId')}
					</Alert>
				</Paper>
			</Box>
		);
	}

	if (isFetching) {
		return (
			<Box className={classes.containerSkeletons}>
				<Stack gap={'25px'}>
					<Skeleton
						height={'30%'}
						width={'60vw'}
						radius="md"
					/>
					<Skeleton
						height={'3%'}
						width={'200px'}
						radius="md"
					/>
					<Skeleton
						height={'5%'}
						width={'100%'}
						radius="md"
					/>
					<Skeleton
						height={'3%'}
						width={'50%'}
						radius="md"
					/>
					<Skeleton
						height={'3%'}
						width={'50%'}
						radius="md"
					/>
					<Skeleton
						height={'3%'}
						width={'50%'}
						radius="md"
					/>
					<Skeleton
						height={'3%'}
						width={'50%'}
						radius="md"
					/>
				</Stack>
				<Skeleton
					height={'100%'}
					width={'100%'}
					radius="md"
				/>
			</Box>
		);
	}

	const measurabilityProject = data;

	if (!measurabilityProject) return null;

	const {
		cover,
		name,
		technology,
		sellPrice,
		description,
		organization,
		entity,
		location,
		social,
		availableStock,
		publicSpecs,
		internalSpecs,
		supportedBy,
	} = measurabilityProject;

	function getSearchableSites(
		sites: TSiteCustom[],
		siteIds: string[]
	) {
		if (Array.isArray(sites) && Array.isArray(siteIds)) {
			const result = sites.filter((_site) =>
				siteIds.includes(_site.id)
			);
			return result;
		}
		return [];
	}

	return (
		<Box className={classes.container} ref={ref}>
			{/**************************** Mint confirmation modal ****************************/}

			{checkRole([
				ROLES.ADMIN,
				ROLES.SUPERADMIN,
				ROLES.STAFF,
			]) && (
				<UpdateMeasurabilityProject
					onClose={() => {
						setUpdateMeasurabilityProjectFormOpened(
							false
						);
					}}
					opened={updateMeasurabilityProjectFormOpened}
				/>
			)}
			<Paper className={classes.paper}>
				<Box className={classes.wrapper}>
					<Box
						className={clsx(
							classes.wrapperLeft,
							userData.role == 'USER' &&
								classes.wrapperLeftFullWidth
						)}
					>
						{/*Bloc Organization */}
						<OrganizationProject
							user={userData}
							contactProducer={contactProducer}
							entity={entity}
							owner={user}
							organization={organization}
							cover={cover}
							supportedBy={supportedBy}
						/>
						{/*Bloc Project informations */}
						<Box className={classes.bloc2}>
							<Box className={classes.rowHeader}>
								<Text className={classes.title}>
									{name}
								</Text>
								<Authorization
									allowedRoles={[
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
										ROLES.STAFF,
									]}
								>
									<Group gap={'12.35px'}>
										<Button
											variant="outline"
											disabled
											leftSection={<IconEye size={23} />}
											size={isBigScreen ? 'md' : 'sm'}
										>
											{t('global.transactionHistory')}
										</Button>
										{checkAcl(
											ACLS.MEASURABILITIES.UPDATE
										) && (
											<ActionIcon
												radius={'7px'}
												variant="outline"
												className={classes.actionIcon}
												aria-label="Settings"
												onClick={() => {
													setUpdateMeasurabilityProjectFormOpened(
														true
													);
													queryClient.invalidateQueries({
														queryKey: [
															'measurability-project-update',
															projectId,
														],
													});
												}}
											>
												<IconEdit stroke={1.67} />
											</ActionIcon>
										)}
										<Button
											disabled
											leftSection={
												<IconCirclePlus size={23} />
											}
											size={isBigScreen ? 'md' : 'sm'}
										>
											{t('global.contribute')}
										</Button>
									</Group>
								</Authorization>
							</Box>
							<Box className={classes.bloc2Informations}>
								<Box
									className={
										classes.groupCountryInformations
									}
								>
									<Avatar
										className={classes.logoCountry}
										radius={'xl'}
										src={`https://flagcdn.com/w40/${location.countryCode.toLowerCase()}.png`}
										size={'xs'}
									>
										<IconQuestionMark />
									</Avatar>

									<Box
										className={
											classes.groupCountryTextInformations
										}
									>
										<Text
											className={
												classes.textCountryProject
											}
										>
											{getCountryName(
												location.countryCode
											)}
										</Text>
										<Text
											className={
												classes.textCountryPlaceProject
											}
										>
											{`/ ${location.place ? location.place : NOT_APPLICABLE}`}
										</Text>
									</Box>
								</Box>
								{/* Technology */}
								<Box className={classes.rowData}>
									<Box className={classes.containerLabel}>
										<Text className={classes.labelText}>
											{t('global.projectType')}
										</Text>
									</Box>
									<Box className={classes.containerValue}>
										<Text className={classes.valueText}>
											{t(technology)}
										</Text>
									</Box>
								</Box>
								{/* Stock disponible */}
								<Box className={classes.rowData}>
									<Box className={classes.containerLabel}>
										<Text className={classes.labelText}>
											{t('global.availableStock')}
										</Text>
									</Box>
									<Box className={classes.containerValue}>
										<Text className={classes.valueText}>
											{availableStock}
										</Text>
									</Box>
								</Box>
								{/* Sale price */}
								{sellPrice && (
									<Box className={classes.rowData}>
										<Box
											className={classes.containerLabel}
										>
											<Text className={classes.labelText}>
												{t('global.sellPrice')}
											</Text>
										</Box>
										<Box
											className={classes.containerValue}
										>
											<Text className={classes.valueText}>
												{sellPrice}
											</Text>
										</Box>
									</Box>
								)}
								{/* Social media */}
								{social &&
									(social.linkedIn || social.twitter) && (
										<Box className={classes.rowData}>
											<Box
												className={classes.containerLabel}
											>
												<Text
													className={classes.labelText}
												>
													{t('global.socialMedia')}
												</Text>
											</Box>
											<Box
												className={classes.containerValue}
											>
												<Group gap={'13.04px'}>
													{social.linkedIn && (
														<Image
															className={
																classes.socialMediaButton
															}
															fit="contain"
															src={LinkedinLogo}
															onClick={() =>
																handleOpenLink(
																	social.linkedIn,
																	'linkedin'
																)
															}
														/>
													)}
													{social.twitter && (
														<Image
															className={
																classes.socialMediaButton
															}
															fit="contain"
															src={XLogo}
															onClick={() =>
																handleOpenLink(
																	social.twitter,
																	'x'
																)
															}
														/>
													)}
												</Group>
											</Box>
										</Box>
									)}

								{/*Public Technical documents */}
								{checkAccess({
									allowedRoles: [
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
										ROLES.STAFF,
										ROLES.USER,
									],
								}) && (
									<Box
										className={
											classes.rowDataTechnicalDocument
										}
									>
										<Box
											className={classes.containerLabel}
										>
											<Text className={classes.labelText}>
												{t('global.technicalDocuments')}
											</Text>
										</Box>
										<Box
											className={classes.containerValue}
										>
											{publicSpecs &&
											Array.isArray(publicSpecs) &&
											publicSpecs.length > 0 ? (
												<Group>
													{publicSpecs.map(
														(
															fileMetaData: TFileMetadata
														) => (
															<Button
																key={fileMetaData.id}
																onClick={() => {
																	window.open(
																		fileMetaData.link,
																		'_blank'
																	);
																}}
																variant="outline"
																leftSection={
																	<IconDownload
																		size={15}
																	/>
																}
																size={'xs'}
															>
																{fileMetaData.filename}
															</Button>
														)
													)}
												</Group>
											) : (
												<Text
													className={classes.valueText}
												>
													{NOT_APPLICABLE}
												</Text>
											)}
										</Box>
									</Box>
								)}

								{/*Internal Technical documents */}
								{checkAccess({
									allowedRoles: [
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
									],
								}) && (
									<Box
										className={
											classes.rowDataTechnicalDocument
										}
									>
										<Box
											className={classes.containerLabel}
										>
											<Text className={classes.labelText}>
												{t(
													'global.technicalDocumentsInternal'
												)}
											</Text>
										</Box>
										<Box
											className={classes.containerValue}
										>
											{!isNil(internalSpecs) &&
											internalSpecs &&
											Array.isArray(internalSpecs) &&
											internalSpecs.length > 0 ? (
												<Group>
													{internalSpecs.map(
														(
															fileMetaData: TFileMetadata
														) => (
															<Button
																key={fileMetaData.id}
																onClick={() => {
																	window.open(
																		fileMetaData.link,
																		'_blank'
																	);
																}}
																variant="outline"
																leftSection={
																	<IconDownload
																		size={15}
																	/>
																}
																size={'xs'}
															>
																{fileMetaData.filename}
															</Button>
														)
													)}
												</Group>
											) : (
												<Text
													className={classes.valueText}
												>
													{NOT_APPLICABLE}
												</Text>
											)}
										</Box>
									</Box>
								)}
								{/* Description */}
								<Box
									className={classes.rowDataDescription}
								>
									<Box
										className={
											classes.containerLabelDescription
										}
									>
										<Text className={classes.labelText}>
											{t('global.description')}
										</Text>
									</Box>
									<Box
										className={
											classes.containerValueDescription
										}
									>
										{description ? (
											<DescriptionRenderer
												description={description}
											/>
										) : (
											<Text className={classes.valueText}>
												{NOT_APPLICABLE}
											</Text>
										)}
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>
					<Authorization
						allowedRoles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
					>
						<Box className={classes.wrapperRight}>
							<Box className={classes.bloc4}>
								<Sites
									projectId={projectId}
									producerId={entity.id}
									data={getSearchableSites(
										sites,
										allSites.map((item: TSite) => item.id)
									)}
									handlesearchsite={(search) => {
										setSiteQuery(search);
									}}
								/>
							</Box>
						</Box>{' '}
					</Authorization>
				</Box>
			</Paper>
		</Box>
	);
};

export default MeasurabilityProjectDetails;
